import { IOrganization } from "@smartrr/shared/entities/Organization";
import { IPurchaseState, PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { getScheduledDeliveriesForCps } from "@smartrr/shared/utils/getScheduledDeliveries";
import compact from 'lodash/compact';
import { useMemo } from "react";
import { StateCreator, create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { DeliveryId } from "./DeliveryId";
import moment from "moment";
import { sequentialWalker } from "@smartrr/shared/utils/sequentialWalker";
import { applySpecialReferralsDiscountToDeliveries } from "@customer-app/utils/specialReferrals";

interface DeliveryStore {
  activeDeliveries: PurchaseStateAndDelivery[];
  pausedDeliveries: PurchaseStateAndDelivery[];

  actions: {
    update(cpsList: IPurchaseState[], organization: IOrganization): void;
  };
}

const deliverStoreSlice: StateCreator<DeliveryStore, [["zustand/immer", never]]> = (set, get) => ({
  activeDeliveries: [],
  pausedDeliveries: [],
  actions: {
    update(cpsList, org) {
      const updatedCpsList = compact(
        cpsList
          .filter(
            cps =>
              !cps.deletedAt && (cps.purchaseStateStatus === "ACTIVE" || cps.purchaseStateStatus === "PAUSED")
          )
          .map(cps => {
            const newCps: IPurchaseState = {
              ...cps,
              stLineItems: cps.stLineItems.filter(li => !li.deletedAt),
            };
            return newCps.stLineItems.length ? newCps : null;
          })
      );

      set(draft => {
        const deliveries: PurchaseStateAndDelivery[] = [];
        draft.pausedDeliveries = [];
        for (const cps of updatedCpsList) {
          if (cps.purchaseStateStatus === "ACTIVE") {
            deliveries.push(
              ...applySpecialReferralsDiscountToDeliveries(
                sequentialWalker(getScheduledDeliveriesForCps(org, cps, 5))
              )
            );
          } else if (cps.purchaseStateStatus === "PAUSED") {
            draft.pausedDeliveries.push(...getScheduledDeliveriesForCps(org, cps, 1, true));
          }
        }

        const sortedDeliveries = [...deliveries].sort((a, b) => {
          const aId = a.purchaseState.id ?? null;
          const bId = b.purchaseState.id ?? null;

          const aDate = moment(a.date).startOf("day").toDate();
          const bDate = moment(b.date).startOf("day").toDate();

          if (aDate === bDate) {
            return aId > bId ? -1 : 1;
          }

          return aDate > bDate ? 1 : -1;
        });

        draft.activeDeliveries = sortedDeliveries;
      });
    },
  },
});

const useDeliveryStore = create<DeliveryStore>()(immer(deliverStoreSlice));

const initialState = useDeliveryStore.getState();

export const DeliveryAccess = {
  useActions() {
    return useDeliveryStore(state => state.actions);
  },
  useHasActiveDeliveries() {
    return !!useDeliveryStore(state => state.activeDeliveries).length;
  },
  useHasPausedDeliveries() {
    return !!useDeliveryStore(state => state.pausedDeliveries).length;
  },
  usePausedDeliveries() {
    return useDeliveryStore(state => state.pausedDeliveries);
  },
  useSortedDeliveries() {
    return useDeliveryStore(state => state.activeDeliveries);
  },
  useDelivery(id: DeliveryId): PurchaseStateAndDelivery | null {
    const activedeliveries = useDeliveryStore(state => state.activeDeliveries);
    const pausedDeliveries = useDeliveryStore(state => state.pausedDeliveries);

    return useMemo(() => {
      if (!id) {
        return null;
      }
      return (
        activedeliveries.find(del => del.purchaseState.id === id.cpsId && del.indexFromNext === id.index) ??
        pausedDeliveries.find(del => del.purchaseState.id === id.cpsId && del.indexFromNext === id.index) ??
        null
      );
    }, [activedeliveries, pausedDeliveries, id]);
  },
  useUpcomingDelivery() {
    const sortedDeliveries = DeliveryAccess.useSortedDeliveries();
    return sortedDeliveries.find(del => !del.isSkipped) ?? null;
  },

  testing: {
    actions: useDeliveryStore.getState().actions,
    state: useDeliveryStore.getState,
    reset: () => useDeliveryStore.setState(initialState),
    initialize(cpsList: IPurchaseState[], organization: IOrganization) {
      DeliveryAccess.testing.reset();
      DeliveryAccess.testing.actions.update(cpsList, organization);
    },
  },
};
